<template>
  <div class="page-container">
    <div class="query-title">积分明细查询报表</div>

    <div class="content table-query">
      <collapse-query2>
        <template slot="content">
          <div class="query-container">
            <el-form inline class="form" size="mini" label-width="100px">
              <el-row :gutter="12">
                <el-col v-if="!isWLY001" :span="8">
                  <el-form-item label="客户编号">
                    <el-input v-model="queryForm.distributerId" clearable />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="瓶码">
                    <el-input v-model="queryForm.bottleCode" clearable />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="产品名称">
                    <el-input v-model="queryForm.productName" clearable />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="积分明细编码">
                    <el-input v-model="queryForm.detailId" clearable />
                  </el-form-item>
                </el-col>
                <el-col :span="16">
                  <el-form-item label="积分获得时间">
                    <el-date-picker
                      v-model="queryForm.dateRange"
                      type="datetimerange"
                      format="yyyy-MM-dd HH:mm:ss"
                      value-format="yyyy-MM-dd HH:mm:ss"
                      :default-time="['00:00:00', '23:59:59']"
                      start-placeholder="开始时间"
                      end-placeholder="结束时间"
                      range-separator="至"
                      clearable
                    />
                  </el-form-item>
                </el-col>
              </el-row>
              <div class="query-actions">
                <el-button size="mini" icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
                <el-button size="mini" type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
              </div>
            </el-form>
          </div>
        </template>
      </collapse-query2>

      <div class="table-container">
        <TableCommon title="查询结果" :custome-config="() => vxeShowCustom('earnTable')">
          <template #button>
            <span v-if="shouldShowStatistics && pointStatistics.total !== undefined && pointStatistics.balance !== undefined" class="point-statistics">
              当前总积分：{{ pointStatistics.total || '0' }}，可用积分：{{ pointStatistics.balance || '0' }}
            </span>
            <table-button v-if="isWLY001" name="add" tip="积分申请" @click="showApplyDialog" />
            <table-button name="export" tip="导出" @click="exportExcel" />
          </template>
          <vxe-grid
            ref="earnTable"
            class="table-block"
            border
            :loading="loading"
            :highlight-hover-row="true"
            resizable
            :show-overflow="true"
            height="400"
            align="center"
            size="mini"
            :toolbar-config="{ custom: true, size: 'mini' }"
            :columns="gridColumns"
            :data="tableData"
          />
          <div class="pagination">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next, jumper"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              :current-page="pagination.currentPage"
              :total="pagination.total"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            />
          </div>
        </TableCommon>
      </div>
    </div>

    <!-- 积分申请对话框 -->
    <el-dialog
      title="积分申请"
      :visible.sync="applyDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="applyForm"
        :model="applyForm"
        :rules="applyFormRules"
        label-width="120px"
        size="small"
      >
        <el-form-item label="当前总积分">
          <el-input v-model="applyForm.total" disabled />
        </el-form-item>
        <el-form-item label="可用积分">
          <el-input v-model="applyForm.balance" disabled />
        </el-form-item>
        <el-form-item label="请输入申请积分" prop="apply">
          <el-input
            v-model.number="applyForm.apply"
            type="number"
            placeholder="请输入申请积分"
            :min="0"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="applyDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="applyLoading" @click="handleApplySubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchDistributerPointEarnPage, fetchDistributerPointStatistics } from '@/api/integralManage/index'
import { apiApplyIntegral } from '@/api/sap/integral-sap'
import { frontExport } from '@/utils/frontExport'
import { mapGetters } from 'vuex'

const POINT_STATUS_TEXT = {
  NORMAL: '正常未使用',
  USED: '已使用',
  PARTIALLY_USED: '部分使用',
  EXPIRED: '已过期'
}

export default {
  name: 'IntegralPointEarnReport',
  computed: {
    ...mapGetters(['customerInfo', 'role']),
    isWLY001() {
      return this.role && this.role.ROLE === 'WLY001'
    },
    shouldShowStatistics() {
      // 如果是WLY001，显示统计信息
      if (this.isWLY001) {
        return true
      }
      // 如果不是WLY001，必须录入了客户编号且已执行查询才显示统计信息
      return !!this.queryForm.distributerId && this.hasSearched
    }
  },
  data() {
    return {
      loading: false,
      queryForm: {
        distributerId: '',
        bottleCode: '',
        productName: '',
        detailId: '',
        dateRange: []
      },
      tableData: [],
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      pointStatistics: {
        total: undefined,
        balance: undefined
      },
      hasSearched: false, // 是否已执行查询
      applyDialogVisible: false,
      applyLoading: false,
      applyForm: {
        total: '',
        balance: '',
        apply: ''
      },
      applyFormRules: {
        apply: [
          { required: true, message: '请输入申请积分', trigger: 'blur' },
          { type: 'number', min: 0, message: '申请积分必须大于等于0', trigger: 'blur' }
        ]
      },
      gridColumns: [
        { type: 'seq', width: 60, title: '序号', align: 'center' },
        { field: 'detailId', title: '积分明细编码', minWidth: 140 },
        { field: 'distributerId', title: '客户编码', minWidth: 140 },
        { field: 'distributerName', title: '客户名称', minWidth: 160 },
        { field: 'bottleCode', title: '瓶码', minWidth: 150 },
        { field: 'productId', title: '产品编号', minWidth: 120 },
        { field: 'productName', title: '产品名称', minWidth: 140 },
        { field: 'earnedPoints', title: '积分数量', minWidth: 110 },
        {
          field: 'pointStatus',
          title: '积分状态',
          minWidth: 110,
          formatter: ({ cellValue }) => POINT_STATUS_TEXT[cellValue] || cellValue || ''
        },
        { field: 'earnedTime', title: '获得时间', minWidth: 170 },
        { field: 'expiryTime', title: '失效时间', minWidth: 170 }
      ]
    }
  },
  created() {
    this.fetchPointStatistics()
    this.fetchData()
  },
  methods: {
    handleSearch() {
      this.hasSearched = true
      // 非WLY001角色，只有录入了客户编号才获取统计信息
      if (this.isWLY001 || this.queryForm.distributerId) {
        this.fetchPointStatistics()
      } else {
        // 如果没有客户编号，清空统计信息
        this.pointStatistics = {
          total: undefined,
          balance: undefined
        }
      }
      this.fetchData(1)
    },
    handleReset() {
      this.queryForm = { 
        distributerId: '', 
        bottleCode: '', 
        productName: '', 
        detailId: '', 
        dateRange: [] 
      }
      // 重置统计信息和查询标志
      this.pointStatistics = {
        total: undefined,
        balance: undefined
      }
      this.hasSearched = false
      // WLY001角色重置后重新获取统计信息
      if (this.isWLY001) {
        this.fetchPointStatistics()
      }
      this.fetchData(1)
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.fetchData(1)
    },
    handleCurrentChange(page) {
      this.fetchData(page)
    },
    buildPayload() {
      const payload = {
        currentpage: String(this.pagination.currentPage - 1),
        pagesize: String(this.pagination.pageSize)
      }
      const { distributerId, bottleCode, productName, detailId, dateRange } = this.queryForm

      // 如果是WLY001，使用当前登录用户的客户编码
      // 如果不是WLY001，使用查询条件中的客户编号，如果没有则使用当前登录用户
      if (this.isWLY001) {
        const currentUserDistributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                                         (this.role && this.role.USER_BP) || ''
        if (currentUserDistributerId) {
          payload.distributerId = String(currentUserDistributerId).trim()
        }
      } else {
        // 非WLY001角色，优先使用查询条件中的客户编号
        if (distributerId) {
          payload.distributerId = distributerId.trim()
        } else {
          // 如果没有输入客户编号，使用当前登录用户
          const currentUserDistributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                                           (this.role && this.role.USER_BP) || ''
          if (currentUserDistributerId) {
            payload.distributerId = String(currentUserDistributerId).trim()
          }
        }
      }

      if (bottleCode) payload.bottleCode = bottleCode.trim()
      if (productName) payload.productName = productName.trim()
      if (detailId) payload.detailId = detailId.trim()
      if (dateRange && dateRange.length === 2) {
        payload.beginTime = dateRange[0]
        payload.endTime = dateRange[1]
      }
      return payload
    },
    async fetchData(page = this.pagination.currentPage) {
      this.loading = true
      this.pagination.currentPage = page
      try {
        const params = this.buildPayload()
        const res = await fetchDistributerPointEarnPage(params)
        if (!res || res.status !== 1) {
          this.tableData = []
          this.pagination.total = 0
          this.$message.error((res && res.msg) || '查询失败')
          return
        }
        const rows = (res.data && res.data.Rows) || []
        const total = (res.data && res.data.Total) || 0
        this.tableData = rows
        this.pagination.total = total
      } catch (error) {
        this.tableData = []
        this.pagination.total = 0
        this.$message.error('查询异常，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    exportExcel() {
      frontExport(this.gridColumns, this.tableData, '积分明细查询报表')
    },
    async fetchPointStatistics() {
      let distributerId = ''
      
      // 如果是WLY001，使用当前登录用户的客户编码
      if (this.isWLY001) {
        distributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                        (this.role && this.role.USER_BP) || ''
      } else {
        // 如果不是WLY001，优先使用查询条件中的客户编号
        if (this.queryForm.distributerId) {
          distributerId = this.queryForm.distributerId.trim()
        } else {
          // 如果没有输入客户编号，使用当前登录用户
          distributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                          (this.role && this.role.USER_BP) || ''
        }
      }
      
      if (!distributerId) {
        return
      }
      try {
        const res = await fetchDistributerPointStatistics({
          distributerId: String(distributerId)
        })
        if (res && res.status === 1 && res.data) {
          this.pointStatistics.total = res.data.total || '0'
          this.pointStatistics.balance = res.data.balance || '0'
        }
      } catch (error) {
        // 静默失败，不影响主查询
      }
    },
    async showApplyDialog() {
      // 获取当前登录用户的客户编码
      const distributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                            (this.role && this.role.USER_BP) || ''
      if (!distributerId) {
        this.$message.warning('无法获取当前登录用户的客户编码')
        return
      }
      this.applyDialogVisible = true
      // 重置表单
      this.applyForm = {
        total: '',
        balance: '',
        apply: ''
      }
      // 获取积分统计信息
      try {
        const res = await fetchDistributerPointStatistics({
          distributerId: String(distributerId)
        })
        if (res && res.status === 1 && res.data) {
          this.applyForm.total = res.data.total || '0'
          this.applyForm.balance = res.data.balance || '0'
        } else {
          this.$message.error((res && res.msg) || '获取积分统计信息失败')
          this.applyDialogVisible = false
        }
      } catch (error) {
        this.$message.error('获取积分统计信息异常，请稍后重试')
        this.applyDialogVisible = false
      }
    },
    async handleApplySubmit() {
      this.$refs.applyForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        // 获取当前登录用户的客户编码
        const distributerId = (this.customerInfo && this.customerInfo.info && this.customerInfo.info.partner) || 
                              (this.role && this.role.USER_BP) || ''
        if (!distributerId) {
          this.$message.warning('无法获取当前登录用户的客户编码')
          return
        }
        if (Number(this.applyForm.apply) > Number(this.applyForm.balance)) {
          this.$message.warning('申请积分不能大于可用积分')
          return
        }
        this.applyLoading = true
        try {
          const params = {
            customer: String(distributerId),
            total: String(this.applyForm.total),
            balance: String(this.applyForm.balance),
            apply: String(this.applyForm.apply)
          }
          const res = await apiApplyIntegral(params)
          if (res && res.msgtype === 'S') {
            this.$message.success(res.message || '积分申请成功')
            this.applyDialogVisible = false
          } else {
            this.$message.error((res && res.message) || '积分申请失败')
          }
        } catch (error) {
          this.$message.error('积分申请异常，请稍后重试')
        } finally {
          this.applyLoading = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.query-title {
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.query-container {
  padding: 0 8px;
}

.query-actions {
  margin-top: 12px;
  display: flex;
  gap: 12px;
}

.pagination {
  margin-top: 12px;
  text-align: right;
}

.point-statistics {
  display: inline-flex;
  align-items: center;
  color: #333;
  font-size: 14px;
  margin-right: 12px;
  font-weight: 400;
}
</style>




// WEBPACK FOOTER //
// src/views/integral-manage/pointEarnReport.vue